import React, { useEffect, useState, useCallback } from 'react';
import {
  Alert,
  Badge,
  Button,
  Card,
  Form,
  Modal,
  Spinner,
  Table
} from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import axiosInstance from 'axiosInstance';
import PageHeader from 'components/common/PageHeader';

const ACCESS_LEVEL_LABELS = {
  view_only: 'PARTNERS.ACCESS.VIEW_ONLY',
  advisor: 'PARTNERS.ACCESS.ADVISOR',
  manager: 'PARTNERS.ACCESS.MANAGER',
  none: 'PARTNERS.ACCESS.NONE'
};

const ACCESS_LEVEL_VARIANTS = {
  view_only: 'info',
  advisor: 'primary',
  manager: 'success',
  none: 'secondary'
};

const BENEFIT_LABELS = {
  discount_only: 'PARTNERS.BENEFIT_OPTIONS.DISCOUNT_ONLY',
  commission_only: 'PARTNERS.BENEFIT_OPTIONS.COMMISSION_ONLY',
  split: 'PARTNERS.BENEFIT_OPTIONS.SPLIT',
};

const PartnerCompaniesPage = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [companies, setCompanies] = useState([]);
  const [error, setError] = useState('');

  // Request access modal
  const [showModal, setShowModal] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [requestLevel, setRequestLevel] = useState('view_only');
  const [requestNote, setRequestNote] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const resp = await axiosInstance.get('login/partner/companies');
      setCompanies(resp.data.companies || []);
    } catch (e) {
      setError(e.response?.data?.error || t('PARTNERS.ERROR_LOAD'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    load();
  }, [load]);

  const openRequestModal = company => {
    setSelectedCompany(company);
    setRequestLevel(company.access?.level || 'view_only');
    setRequestNote('');
    setSubmitError('');
    setShowModal(true);
  };

  const handleRequestSubmit = async () => {
    setSubmitting(true);
    setSubmitError('');
    try {
      await axiosInstance.post(
        `login/partner/companies/${selectedCompany.id}/request-access`,
        { access_level: requestLevel, note: requestNote }
      );
      setShowModal(false);
      load();
    } catch (e) {
      setSubmitError(e.response?.data?.error || t('PARTNERS.ACCESS.REQUEST_ERROR'));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center py-6">
        <Spinner animation="border" />
      </div>
    );
  }

  return (
    <>
      <PageHeader
        title={t('PARTNERS.COMPANIES.TITLE')}
        description={t('PARTNERS.COMPANIES.DESCRIPTION')}
        className="mb-4"
      />

      {error ? <Alert variant="danger">{error}</Alert> : null}

      {companies.length === 0 ? (
        <Card className="border">
          <Card.Body className="text-center text-600 py-5">
            <FontAwesomeIcon icon="building" className="fs-3 mb-3 d-block mx-auto text-400" />
            {t('PARTNERS.COMPANIES.EMPTY')}
          </Card.Body>
        </Card>
      ) : (
        <Card className="border">
          <Card.Body className="p-0">
            <Table responsive className="mb-0 fs--1">
              <thead className="bg-200 text-900">
                <tr>
                  <th>{t('PARTNERS.COMPANIES.COL_NAME')}</th>
                  <th>{t('PARTNERS.COMPANIES.COL_OWNER')}</th>
                  <th>{t('PARTNERS.COMPANIES.COL_JOINED')}</th>
                  <th>{t('PARTNERS.COMPANIES.COL_BENEFIT_TYPE')}</th>
                  <th>{t('PARTNERS.COMPANIES.COL_PARTNER_BENEFIT')}</th>
                  <th>{t('PARTNERS.COMPANIES.COL_COMPANY_BENEFIT')}</th>
                  <th>{t('PARTNERS.COMPANIES.COL_ACCESS')}</th>
                  <th className="text-end">{t('PARTNERS.COMPANIES.COL_ACTIONS')}</th>
                </tr>
              </thead>
              <tbody>
                {companies.map(company => {
                  const access = company.access;
                  return (
                    <tr key={company.id}>
                      <td className="align-middle fw-semi-bold">{company.name}</td>
                      <td className="align-middle text-600">{company.owner}</td>
                      <td className="align-middle text-600">
                        {new Date(company.created_at).toLocaleDateString()}
                      </td>
                      <td className="align-middle text-600">
                        {company.benefit_choice
                          ? t(BENEFIT_LABELS[company.benefit_choice] || 'PARTNERS.BENEFIT_OPTIONS.UNKNOWN')
                          : '—'}
                      </td>
                      <td className="align-middle text-600">
                        {company.partner_commission_pct !== ''
                          ? `${company.partner_commission_pct}%`
                          : '—'}
                      </td>
                      <td className="align-middle text-600">
                        {company.company_discount_pct !== ''
                          ? `${company.company_discount_pct}%`
                          : '—'}
                      </td>
                      <td className="align-middle">
                        {access ? (
                          <div>
                            <Badge
                              bg={access.is_active ? ACCESS_LEVEL_VARIANTS[access.level] || 'secondary' : 'secondary'}
                            >
                              {t(ACCESS_LEVEL_LABELS[access.level] || access.level)}
                            </Badge>
                            {!access.is_active && (
                              <div className="text-600 fs--2 mt-1">
                                {t('PARTNERS.ACCESS.PENDING_APPROVAL')}
                              </div>
                            )}
                          </div>
                        ) : (
                          <span className="text-600 fs--1">{t('PARTNERS.ACCESS.NO_ACCESS')}</span>
                        )}
                      </td>
                      <td className="align-middle text-end">
                        <Button
                          size="sm"
                          variant="outline-primary"
                          onClick={() => openRequestModal(company)}
                        >
                          {access && !access.is_active
                            ? t('PARTNERS.ACCESS.EDIT_REQUEST')
                            : access
                            ? t('PARTNERS.ACCESS.CHANGE_LEVEL')
                            : t('PARTNERS.ACCESS.REQUEST')}
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </Card.Body>
        </Card>
      )}

      <Modal show={showModal} onHide={() => setShowModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title className="fs-0">
            {t('PARTNERS.ACCESS.REQUEST_MODAL_TITLE', { company: selectedCompany?.name })}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {submitError ? <Alert variant="danger">{submitError}</Alert> : null}
          <Form.Group className="mb-3">
            <Form.Label>{t('PARTNERS.ACCESS.LEVEL_LABEL')}</Form.Label>
            <Form.Select
              value={requestLevel}
              onChange={e => setRequestLevel(e.target.value)}
            >
              <option value="view_only">{t('PARTNERS.ACCESS.VIEW_ONLY')}</option>
              <option value="advisor">{t('PARTNERS.ACCESS.ADVISOR')}</option>
              <option value="manager">{t('PARTNERS.ACCESS.MANAGER')}</option>
            </Form.Select>
            <Form.Text className="text-muted">
              {t('PARTNERS.ACCESS.LEVEL_HELP')}
            </Form.Text>
          </Form.Group>
          <Form.Group>
            <Form.Label>{t('PARTNERS.ACCESS.NOTE_LABEL')}</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={requestNote}
              onChange={e => setRequestNote(e.target.value)}
              placeholder={t('PARTNERS.ACCESS.NOTE_PLACEHOLDER')}
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)} disabled={submitting}>
            {t('COMMON.CANCEL', { defaultValue: 'Cancelar' })}
          </Button>
          <Button variant="primary" onClick={handleRequestSubmit} disabled={submitting}>
            {submitting ? <Spinner size="sm" animation="border" /> : t('PARTNERS.ACCESS.SEND_REQUEST')}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default PartnerCompaniesPage;
